import { isEqual } from 'lodash';
import * as React from 'react';

import { useGetMessageContext } from 'bones/components/molecules/Voicebox/context/panelData/useGetMessageContext';
import { VoiceboxMessage } from 'bones/constants/voicebox/message';
import {
  VoiceboxPanelAnalysisData,
  VoiceboxPanelDataContextValue,
} from 'bones/constants/voicebox/panel';
import { VoiceboxCopy } from 'bones/constants/voicebox/schema';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { usePortalVoiceboxAnalysis } from 'bones/utils/voicebox/usePortalVoiceboxAnalysis';

export const useRequestAnalysis = ({
  copy,
  connectionId,
  connectionIdx,
  conversationId,
  messages,
  setAnalysisDataByMessageId,
}: {
  copy: VoiceboxCopy;
  connectionId: string;
  connectionIdx: number;
  conversationId: string;
  messages: VoiceboxMessage[];
  setAnalysisDataByMessageId: React.Dispatch<
    React.SetStateAction<
      VoiceboxPanelDataContextValue['analysisDataByMessageId']
    >
  >;
}) => {
  const isMounted = useIsMounted();

  const setAnalysisDataForMessageId = React.useCallback(
    (
      messageId: string,
      action: React.SetStateAction<
        Partial<{
          key: string;
          analysisDataByKey: Record<
            string,
            VoiceboxPanelAnalysisData | undefined
          >;
        }>
      >
    ) => {
      // istanbul ignore if
      if (!isMounted.current) {
        return;
      }

      setAnalysisDataByMessageId((prevById) => {
        const prevData = prevById[messageId] || {
          key: '',
          analysisDataByKey: {
            '': {
              analysis: '',
              data: '',
              error: '',
              loading: false,
            },
          },
        };
        return {
          ...prevById,
          [messageId]: {
            ...prevData,
            ...(typeof action === 'function'
              ? action(prevData)
              : /* istanbul ignore next */ action),
          },
        };
      });
    },
    [isMounted, setAnalysisDataByMessageId]
  );

  const [analysisQueryArgs, setAnalysisQueryArgs] = React.useState<{
    messageId: string;
    context: Record<string, any>;
    analysis: string;
  } | null>(null);
  const {
    isFetching: analysisQueryIsFetching,
    data: analysisQueryData,
    refetch: refetchAnalysisQuery,
  } = usePortalVoiceboxAnalysis(
    connectionId,
    connectionIdx,
    conversationId,
    analysisQueryArgs?.messageId || '',
    analysisQueryArgs?.context || {},
    analysisQueryArgs?.analysis || '',
    {
      enabled: Boolean(analysisQueryArgs),
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );
  React.useEffect(() => {
    if (analysisQueryIsFetching) {
      return;
    }

    if (!analysisQueryData) {
      return;
    }

    const { messageId, analysis, result = '', error } = analysisQueryData;
    if (error) {
      setAnalysisDataForMessageId(messageId, (prev) => ({
        analysisDataByKey: {
          ...prev.analysisDataByKey,
          [analysis]: {
            analysis,
            data: result,
            error: error || /* istanbul ignore next */ copy.errors.unknownError,
            loading: false,
          },
        },
      }));
      return;
    }

    setAnalysisDataForMessageId(messageId, (prev) => ({
      analysisDataByKey: {
        ...prev.analysisDataByKey,
        [analysis]: {
          analysis,
          data: result,
          error: '',
          loading: false,
        },
      },
    }));
  }, [
    analysisQueryData,
    analysisQueryArgs,
    analysisQueryIsFetching,
    copy,
    setAnalysisDataForMessageId,
  ]);

  const getMessageContext = useGetMessageContext(messages);

  const requestAnalysis = React.useCallback(
    async (messageId: string, analysis: string) => {
      // istanbul ignore if
      if (!analysis) {
        return;
      }

      // load new analysis
      setAnalysisDataForMessageId(messageId, (prev) => ({
        analysisDataByKey: {
          ...prev.analysisDataByKey,
          [analysis]: {
            analysis,
            data: '',
            error: '',
            loading: true,
          },
        },
      }));

      setAnalysisQueryArgs((prev) => {
        const next = {
          messageId,
          context: getMessageContext(messageId),
          analysis,
        };

        if (isEqual(prev, next)) {
          refetchAnalysisQuery();
          return prev;
        }

        return next;
      });
    },
    [getMessageContext, refetchAnalysisQuery, setAnalysisDataForMessageId]
  );

  const setAnalysis = React.useCallback(
    async (messageId: string, analysis: string) => {
      setAnalysisDataForMessageId(messageId, {
        key: analysis,
      });
    },
    [setAnalysisDataForMessageId]
  );

  return { requestAnalysis, setAnalysis };
};
